import { useState, useEffect } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Card, CardMedia, CardContent, Typography, Checkbox, FormControlLabel, TextField, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTheme, useMediaQuery } from '@mui/material';
import { getServiceTypes } from '@services/planService';
import { SelectChangeEvent } from '@mui/material/Select';
import type { ServiceType } from '@app-types/models/plan';

export interface InstagramPost {
  id: string;
  mediaUrl: string;
  mediaType: string;
  caption?: string;
  permalink?: string;
  timestamp?: string;
  hashtags?: string[];
  [key: string]: unknown;
}

interface InstagramPostsDialogProps {
  open: boolean;
  onClose: () => void;
  posts: InstagramPost[];
  selectedPosts: string[];
  onPostSelection: (postId: string) => void;
  onSync: (serviceTypeId: string) => void;
  isLoading: boolean;
  isSyncing: boolean;
  onCaptionChange: (postId: string, caption: string) => void;
  onDisconnect: () => void;
}

const InstagramPostsDialog: React.FC<InstagramPostsDialogProps> = ({
  open,
  onClose,
  posts,
  selectedPosts,
  onPostSelection,
  onSync,
  isLoading,
  isSyncing,
  onCaptionChange,
  onDisconnect
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState('');

  useEffect(() => {
    if (open) {
      getServiceTypes().then((response) => setServiceTypes((Array.isArray(response) ? response : []) as ServiceType[])).catch(() => {});
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        {t('showcaseManagement.selectInstagramPosts')}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {serviceTypes.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="ig-sync-service-type-label">
                  {t('showcaseForm.serviceType', 'Service Type (optional)')}
                </InputLabel>
                <Select
                  labelId="ig-sync-service-type-label"
                  label={t('showcaseForm.serviceType', 'Service Type (optional)')}
                  value={selectedServiceTypeId}
                  onChange={(e: SelectChangeEvent<string>) => setSelectedServiceTypeId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{t('showcaseForm.noServiceType', 'None -- AI will classify later')}</em>
                  </MenuItem>
                  {serviceTypes.map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                      {type.name} ({type.zh_name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Grid container spacing={2}>
              {posts && posts.map((post) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.id}>
                  <Card>
                    {post.mediaType === 'video' ? (
                      <CardMedia
                        component="video"
                        height="200"
                        controls
                        src={post.mediaUrl}
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <CardMedia
                        component="img"
                        height="200"
                        image={post.mediaUrl}
                        alt={post.caption || t('showcase.instagramPostAlt')}
                      />
                    )}
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedPosts.includes(post.id)}
                            onChange={() => onPostSelection(post.id)}
                          />
                        }
                        label={t('showcaseManagement.selectPost')}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        size="small"
                        value={post.caption || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCaptionChange(post.id, e.target.value)}
                        placeholder={t('showcaseManagement.enterCaption')}
                        sx={{ mt: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {post.timestamp ? new Date(post.timestamp).toLocaleDateString() : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 1, boxShadow: '0 -2px 8px rgba(0,0,0,0.04)', justifyContent: 'space-between' }}>
        <Button
          startIcon={<LinkOffIcon />}
          onClick={onDisconnect}
          color="error"
          variant="outlined"
        >
          {t('showcaseManagement.disconnectInstagram')}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>
            {t('commonConfirmationDialog.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => onSync(selectedServiceTypeId)}
            disabled={selectedPosts.length === 0 || isSyncing}
          >
            {isSyncing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('showcaseManagement.syncingInstagram')}
              </>
            ) : (
              t('showcaseManagement.syncSelectedPosts')
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default InstagramPostsDialog;